import { LandingInput } from '../../../../components/cells/LandingInput/LandingInput';
import { Enable, $ as $E, html, rune } from 'rune-ts';
import { PlusShopEditor } from './PlusShopEditor';
import { Alert, AlertProps } from '../../../../../shared/components/cells/Alert/Alert';
import { checkPhoneNumberSms, isValidEmail } from '../../../../../shared/util/validation';
import { PlusShopEditorText } from './PlusShopEditorText';
import { LandingInputFile } from '../../../../components/cells/LandingInputFile/LandingInputFile';
import { LandingDropdown } from '../../../../components/cells/LandingDropdown/LandingDropdown';
import { ButtonAction } from '../../../../../shared/components/atoms/ButtonAction/ButtonAction';
import klass from './PlusShopEditor.module.scss';
import { InputResetEvent } from '../../../../components/atoms/Input/InputEvent';

export class PlusShopEditorController extends Enable {
  is_marketing = false;
  is_agree = false;
  inputManager: LandingInput;
  inputCompany: LandingInput;
  inputCompanyCategory: LandingDropdown;
  inputManagerEmail: LandingInput;
  inputManagerPhone: LandingInput;
  inputOther: LandingInput;
  inputFile: LandingInputFile;
  submitButton: ButtonAction;

  constructor(public override view: PlusShopEditor) {
    super(view);
    const is_mobile = !!rune.getSharedData(view)?.is_mobile;

    const texts = new PlusShopEditorText().getTexts(is_mobile);

    this.inputCompany = new LandingInput({
      name: 'company_name',
      delete_pattern: /[^ㄱ-ㅎ가-힣a-zA-Z0-9]+/g,
      required: true,
      placeholder: texts.submit_form_company,
    });
    this.inputCompanyCategory = new LandingDropdown({
      options: [
        {
          label: texts.enterprise_category,
          value: '',
        },
        {
          label: texts.private_enterprise,
          value: '개인기업',
        },
        {
          label: texts.corporate_enterprise,
          value: '법인기업',
        },
        {
          label: texts.public_enterprise,
          value: '공기업',
        },
        {
          label: texts.social_enterprise,
          value: '사회적기업',
        },
        {
          label: texts.etc_enterprise,
          value: '기타',
        },
      ],
      place_holder: texts.submit_form_company_category,
      required: true,
      name: 'company_category',
    });
    this.inputManager = new LandingInput({
      name: 'manager_name',
      delete_pattern: /[^ㄱ-ㅎ가-힣a-zA-Z0-9]+/g,
      required: true,
      placeholder: texts.submit_form_manager,
    });

    this.inputManagerEmail = new LandingInput({
      name: 'manager_email',
      type: 'email',
      delete_pattern: /[^a-zA-Z0-9@._-]+/g,
      required: true,
      placeholder: texts.submit_form_manager_email,
      error_helper: {
        message: texts.submit_form_email_error,
        isError: (value) => !isValidEmail(value),
      },
    });

    this.inputManagerPhone = new LandingInput({
      name: 'manager_phone',
      type: 'tel',
      maxlength: 11,
      delete_pattern: /\D/g,
      required: true,
      placeholder: texts.submit_form_manager_phone,
      error_helper: {
        message: texts.submit_form_phone_error,
        isError: (value) => !checkPhoneNumberSms(value),
      },
    });
    this.inputFile = new LandingInputFile({
      placeholder: texts.submit_form_file,
      name: 'file',
      helper_message: ET('mps2::landing::file::helper_message'),
    });
    this.inputOther = new LandingInput({
      name: 'body',
      placeholder: texts.submit_form_other,
    });
    this.submitButton = new ButtonAction({
      type: 'disabled',
      label: texts.submit_form_button,
      klass: klass.submit,
      size: 'large',
    });
  }

  private _isInvalidInput = (selector = 'input', checkFn: (value: string) => boolean) => {
    return $E(this.element())
      .findAll(selector)
      .find((el) => checkFn(el.getValue()));
  };

  private _isValidInputForm() {
    return (
      this.is_agree &&
      !this._isInvalidInput('input[required]', (value) => !value) &&
      !this._isInvalidInput("input[type='email']", (value) => !isValidEmail(value)) &&
      !this._isInvalidInput("input[type='tel']", (value) => !checkPhoneNumberSms(value))
    );
  }

  async isValidInputFormWithModal() {
    if (!this.is_agree) {
      await this._alertOpenWithFocusIn({
        title: '약관 동의 확인',
        message: html.preventEscape('신청 완료를 위해<br/>개인정보 제공을 동의해 주세요.'),
      });
      return false;
    }

    if (this._isInvalidInput('input[required]', (value) => !value)) {
      await this._alertOpenWithFocusIn({
        title: '필수사항 미입력',
        message: '필수 사항을 입력해주세요.',
      });
      return false;
    }

    if (this._isInvalidInput("input[type='email']", (value) => !isValidEmail(value))) {
      await this._alertOpenWithFocusIn({
        title: '이메일 형식 확인',
        message: html.preventEscape('이메일 형식이 잘못되었습니다.<br/>다시 입력해주세요.'),
      });
      return false;
    }

    if (this._isInvalidInput("input[type='tel']", (value) => !checkPhoneNumberSms(value))) {
      await this._alertOpenWithFocusIn({
        title: '전화번호 형식 확인',
        message: html.preventEscape('전화번호 형식을 확인해주세요.<br/>다시 입력해주세요.'),
      });
      return false;
    }

    return true;
  }

  private _alertOpenWithFocusIn = (props: AlertProps) => {
    const activeElement = document.activeElement;
    if (activeElement instanceof HTMLElement) {
      activeElement.blur();
    }
    return Alert.open(props);
  };

  setValidationInHtml() {
    if (this._isValidInputForm()) {
      this.view.setIsValid(true);
      this.submitButton.setTypeOption('blue');
    } else {
      this.view.setIsValid(false);
      this.submitButton.setTypeOption('disabled');
    }
  }

  protected override onRender() {
    this.delegate('change', 'input', () => {
      this.setValidationInHtml();
    });
    this.addEventListener(InputResetEvent, () => {
      this.setValidationInHtml();
    });
  }
}
